import {
  ComposedChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Bar,
  Cell,
} from 'recharts';
import { useDarkMode } from '../../context/DarkModeContext';

function fmt(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

const CandlestickShape = (props) => {
  const { x, width, y, height, payload } = props;
  const { option_close: close, option_open: open, option_high: high, option_low: low } = payload;

  if (open == null || high == null || low == null) {
    return <line x1={x} x2={x + width} y1={y} y2={y} stroke="#6b7280" strokeWidth={1.5} />;
  }

  const isGreen = close >= open;
  const bodyColor = isGreen ? '#22c55e' : '#ef4444';
  const wickColor = isGreen ? '#16a34a' : '#dc2626';

  const bodyTop    = y;
  const bodyBottom = y + height;
  const bodyX      = x + width * 0.15;
  const bodyWidth  = width * 0.7;
  const cx         = x + width / 2;

  const bodyPriceRange = Math.abs(close - open);
  const pixelsPerUnit  = bodyPriceRange > 0 ? height / bodyPriceRange : 1;

  const maxOC = Math.max(open, close);
  const minOC = Math.min(open, close);

  const highPixel = bodyTop    - (high - maxOC) * pixelsPerUnit;
  const lowPixel  = bodyBottom + (minOC - low)  * pixelsPerUnit;

  return (
    <g>
      <line x1={cx} x2={cx} y1={highPixel} y2={bodyTop}    stroke={wickColor} strokeWidth={1.5} />
      <rect x={bodyX} y={bodyTop} width={bodyWidth} height={Math.max(height, 1)}
            fill={bodyColor} stroke={wickColor} strokeWidth={0.5} rx={1} />
      <line x1={cx} x2={cx} y1={bodyBottom} y2={lowPixel}  stroke={wickColor} strokeWidth={1.5} />
    </g>
  );
};

const CandleTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  if (!d) return null;
  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-3 shadow-lg text-xs min-w-[130px]">
      <p className="font-semibold text-gray-700 dark:text-gray-200 mb-1.5">{fmt(label)}</p>
      {d.option_open != null && (
        <>
          <div className="flex justify-between gap-3"><span className="text-gray-400">Open</span><span className="font-medium">₹{d.option_open.toFixed(2)}</span></div>
          <div className="flex justify-between gap-3"><span className="text-gray-400">High</span><span className="font-medium text-green-600">₹{d.option_high?.toFixed(2)}</span></div>
          <div className="flex justify-between gap-3"><span className="text-gray-400">Low</span><span className="font-medium text-red-500">₹{d.option_low?.toFixed(2)}</span></div>
        </>
      )}
      <div className="flex justify-between gap-3"><span className="text-gray-400">Close</span><span className="font-bold">₹{d.option_close?.toFixed(2)}</span></div>
    </div>
  );
};

export default function OptionPriceChart({ data, height = 240, syncId }) {
  const { dark } = useDarkMode();

  if (!data || data.length === 0) return null;

  const hasOHLC = data.some((d) => d.option_open != null);

  const grid = dark ? '#374151' : '#e5e7eb';
  const axis = dark ? '#6b7280' : '#9ca3af';

  const chartData = data.map((d) => ({
    ...d,
    body: d.option_open != null
      ? [Math.min(d.option_open, d.option_close), Math.max(d.option_open, d.option_close)]
      : [d.option_close - 0.5, d.option_close + 0.5],
  }));

  const allPrices = data.flatMap((d) => [
    d.option_close, d.option_open, d.option_high, d.option_low,
  ]).filter(Boolean);
  const priceMin = Math.min(...allPrices);
  const priceMax = Math.max(...allPrices);
  const pad = (priceMax - priceMin) * 0.05 || 1;
  const domain = [Math.max(0, priceMin - pad), priceMax + pad];

  return (
    <ResponsiveContainer width="100%" height={height}>
      <ComposedChart data={chartData} syncId={syncId} margin={{ top: 8, right: 12, bottom: 24, left: 8 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={grid} />
        <XAxis
          dataKey="date"
          tickFormatter={fmt}
          tick={{ fontSize: 10, fill: axis }}
          stroke={axis}
          angle={-30}
          textAnchor="end"
          height={44}
        />
        <YAxis
          tickFormatter={(v) => `₹${v.toFixed(0)}`}
          tick={{ fontSize: 10, fill: axis }}
          stroke={axis}
          width={48}
          domain={domain}
          scale="linear"
        />
        <Tooltip content={<CandleTooltip />} />
        {hasOHLC ? (
          <Bar dataKey="body" shape={<CandlestickShape />} isAnimationActive={false} maxBarSize={28}>
            {chartData.map((entry, i) => (
              <Cell key={i} fill={entry.option_close >= (entry.option_open ?? entry.option_close) ? '#22c55e' : '#ef4444'} />
            ))}
          </Bar>
        ) : (
          <Bar dataKey="option_close" fill="#a855f7" maxBarSize={12} radius={2} />
        )}
      </ComposedChart>
    </ResponsiveContainer>
  );
}
